import { Chip } from "@mui/material";
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  DateInput,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleShowLayout,
  TextField,
} from "react-admin";
import { grants } from "../../backoffice.access_control";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { AccessDenied } from "../../misc/AccessDenied";
import { DefaultList } from "../../misc/DefaultList";
import { EmptyState } from "../../misc/EmptyState";
import { formatMoney } from "../../misc/Money";
import { PageTitle } from "../../misc/PageTitle";
import type { ServiceEntry } from "../../providers/serviceEntriesProvider";

const statuses: Record<ServiceEntry["status"], { label: string; color: "default" | "success" | "warning" }> = {
  PENDING: { label: "Offen", color: "warning" },
  INVOICED: { label: "Abgerechnet", color: "success" },
  CANCELED: { label: "Storniert", color: "default" },
};
const parties: Record<ServiceEntry["issuerType"], string> = {
  AUTOVIO: "AUTOVIO",
  DRIVING_SCHOOL: "Fahrschule",
  STUDENT: "Fahrschüler",
  COST_BEARER: "Kostenträger",
};

export function DrivingSchoolServiceEntries() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const drivingSchool = useCurrentDrivingSchool();
  if (
    !grants.has("developer") &&
    (!grants.has("ServiceEntry:list") || !drivingSchool.customizations.includes("nextGenerationInvoicing"))
  ) {
    return <AccessDenied />;
  }
  if (!drivingSchoolId) {
    return <DefaultList />;
  }
  return (
    <List
      resource="serviceEntries"
      title={<PageTitle>Leistungen</PageTitle>}
      storeKey={`DrivingSchoolServiceEntries@${drivingSchoolId}`}
      filter={{ drivingSchoolId }}
      sort={{ field: "serviceDate", order: "DESC" }}
      perPage={25}
      exporter={false}
      hasCreate={false}
      empty={<EmptyState />}
      filters={[
        <SelectInput
          key="status"
          source="status"
          label="Status"
          alwaysOn
          choices={Object.entries(statuses).map(([id, status]) => ({ id, name: status.label }))}
        />,
        <ReferenceInput key="studentId" source="studentId" reference="students" filter={{ drivingSchoolId }} alwaysOn>
          <AutocompleteInput label="Fahrschüler" optionText="name" />
        </ReferenceInput>,
        <DateInput key="dateFrom" source="dateFrom" label="Leistungsdatum von" alwaysOn />,
        <DateInput key="dateUntil" source="dateUntil" label="Leistungsdatum bis" alwaysOn />,
      ]}
    >
      <Datagrid bulkActionButtons={false} rowClick="expand" expand={<ServiceEntryDetails />} empty={<EmptyState />}>
        <DateField
          source="serviceDate"
          label="Leistungsdatum"
          locales="de-DE"
          options={{ timeZone: "Europe/Berlin" }}
        />
        <TextField source="description" label="Leistung" sortable={false} />
        <ReferenceField
          source="studentId"
          reference="students"
          label="Fahrschüler"
          link="edit"
          sortable={false}
          emptyText="–"
        />
        <TextField source="drivingLicenseClass" label="Klasse" emptyText="–" />
        <FunctionField<ServiceEntry>
          source="status"
          label="Status"
          render={(entry) => (
            <Chip size="small" label={statuses[entry.status].label} color={statuses[entry.status].color} />
          )}
        />
        <NumberField source="quantity" label="Menge" sortable={false} locales="de-DE" />
        <FunctionField<ServiceEntry>
          source="totalGross"
          sortable={false}
          label="Brutto"
          textAlign="right"
          render={(entry) => formatMoney(entry.totalGross, entry.currency)}
        />
      </Datagrid>
    </List>
  );
}

function ServiceEntryDetails() {
  return (
    <SimpleShowLayout>
      <FunctionField<ServiceEntry>
        label="Einzelpreis netto"
        render={(entry) => formatMoney(entry.unitPriceNet, entry.currency)}
      />
      <FunctionField<ServiceEntry>
        label="Einzelpreis brutto"
        render={(entry) => formatMoney(entry.unitPriceGross, entry.currency)}
      />
      <FunctionField<ServiceEntry>
        label="Gesamt netto"
        render={(entry) => formatMoney(entry.totalNet, entry.currency)}
      />
      <FunctionField<ServiceEntry>
        label="MwSt."
        render={(entry) =>
          `${formatMoney(entry.vatAmount, entry.currency)} (${Number(entry.vatRate).toLocaleString("de-DE")} %)`
        }
      />
      <FunctionField<ServiceEntry> label="Leistungserbringer" render={(entry) => parties[entry.issuerType]} />
      <FunctionField<ServiceEntry> label="Empfänger" render={(entry) => parties[entry.recipientType]} />
      <ReferenceField source="instructorId" reference="instructors" label="Fahrlehrer" link="edit" emptyText="–" />
      <ReferenceField source="branchId" reference="branches" label="Filiale" link={false} emptyText="–" />
      <TextField source="calendarEventId" label="Termin-ID" emptyText="–" />
      <DateField
        source="createdAt"
        label="Erfasst am"
        showTime
        locales="de-DE"
        options={{ timeZone: "Europe/Berlin" }}
      />
      <DateField
        source="updatedAt"
        label="Zuletzt aktualisiert"
        showTime
        locales="de-DE"
        options={{ timeZone: "Europe/Berlin" }}
      />
    </SimpleShowLayout>
  );
}
